import {Translator} from "../../../core/setting/Translator";
import {TranslationTexts} from "../../config/translate/TranslationTexts";
import {gsap, Power3} from "gsap";
import {TextStyleOptions} from "../../../core/setting/text/IText";
import {FontStyle, FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {PunkRocker} from "../../PunkRocker";
import {PREngineManager} from "../../punkEngine/PREngineManager";
import {Helper} from "../../../core/utils/Helper";
import {AnimationEffects} from "../../../core/effects/AnimationEffects";
import {LayoutDimData} from "../../../core/utils/ResizeHelper";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {XWayStateLevel} from "./trains/XWayStateLevel";
import {GameData} from "../../server/data/GameData";
import {PRGameMode} from "../../server/PRGameMode";

export class WaysCounter extends PIXI.Container {
    protected waysText: string = "Ways";
    protected value: number = 729;
    protected _itemsToHide: PIXI.DisplayObject[] = [];
    protected _textCounter: PIXI.Text;
    protected _countsUp: boolean = true;
    protected _xWayState: XWayStateLevel = new XWayStateLevel(XWayStateLevel.LEVEL_1);
    protected HEADER_TEXTURE_PATH: string = "Header/";
    protected _headerBg: PIXI.Sprite;
    protected _spineLoop: PIXI.spine.Spine;
    protected _riotSpine: PIXI.spine.Spine;
    protected _timeLine: gsap.core.Timeline;
    protected _textTimeline: gsap.core.Timeline;
    protected _textPost: PIXI.Text;
    protected freeSpins: number = 0;
    private tlText: gsap.core.Timeline;
    private bottomTrainVisible: boolean = false;
    private readonly DEFAULT_TEXT_STYLE: TextStyleOptions = {
        fontFamily: "Helvetica",
        fontStyle: FontStyle.ITALIC,
        fontSize: 42,
        fontWeight: FontWeight.EXTRA_BOLD,
        dropShadow: true,
        dropShadowAngle: 8,
        dropShadowBlur: 4,
        dropShadowDistance: 10,
        fill: "#ffe8d9",
    };

    private _gameData: GameData;

    constructor() {
        super();
        this.name = "WaysCounter";

        this.init();
        this.onResize();
    }

    get headerTexture(): PIXI.Texture {
        return PunkRocker.assetLoader.getTextures(this.HEADER_TEXTURE_PATH + this._xWayState.TextureLvl)[0];
    }

    onResize() {
        this.position.set(360, 90);

        const scaledReelDim: LayoutDimData = this.bottomTrainVisible ? PREngineManager.dimScaled : PREngineManager.dim;

        if (NolimitApplication.isLandscape && this.bottomTrainVisible) {
            this.position.set(360, 85);
        } else {
            this.position.set(360, 90);
        }
        this.scale.set(scaledReelDim.scale.x, scaledReelDim.scale.y);
    }

    public setGameData(data: GameData): void {
        this._gameData = data;
    }

    public setValue(from: number | undefined, to: number, duration: number): gsap.core.Timeline {
        this._timeLine = Helper.setTimeline(this._timeLine, new gsap.core.Timeline());

        if (!this._textCounter.visible) {
            return this._timeLine;
        }

        if (this.value === to) {
            if (this.isFirstSpinAfterBonusGame() || this._gameData.raw.brokeTheBank) {
                this._textPost.alpha = 0;
                this._textCounter.alpha = 1;
            }
            return new gsap.core.Timeline();
        }

        let fromValue = from != undefined ? from : this.value;

        this._countsUp = to > fromValue;

        this._timeLine = new gsap.core.Timeline({
            onUpdate: () => {
                this.updateValue();
            },
            onComplete: () => {
                this.updateValue();
            }
        });
        this._timeLine.fromTo(this, {
            value: fromValue
        }, {
            value: to,
            duration:
            duration
        });
        return this._timeLine;
    }

    public updateValue(): void {
        this._textCounter.text = this.value.toFixed(0);
        this._textCounter.text += this.waysText;
        if (!this._textCounter.visible) return;

        if (this._countsUp) {
            if (this.value > this._xWayState.valueLvl && this._xWayState.set(this._xWayState.value + 1)) {
                gsap.to(this._textCounter.scale, {duration: 0.15, x: 1.2, y: 1.2}).eventCallback("onComplete", () => {
                    gsap.to(this._textCounter.scale, {duration: 0.15, x: 1, y: 1})
                })
                this.playLoop();
            }
        } else {
            if (this.value < this._xWayState.lastLvl && this._xWayState.set(this._xWayState.value - 1)) {
                gsap.to(this._textCounter.scale, {duration: 0.1, x: 0.95, y: 0.95}).eventCallback("onComplete", () => {
                    gsap.to(this._textCounter.scale, {duration: 0.1, x: 1, y: 1})
                })
                if (this._xWayState.equal(XWayStateLevel.LEVEL_1)) {
                    this.stopLoop();
                } else
                    this.playLoop();
            }
        }

        this._headerBg.texture = this.headerTexture;
        this._textCounter.updateTransform();
        this._textCounter.anchor.set(0.5, 0.5);

        if (this._textCounter.width > 500) this._textCounter.style.fontSize = 34;
    }

    public playLoop(): void {
        this._spineLoop.visible = true;
        this._spineLoop.state.setAnimation(0, this._xWayState.SpineLvl, true);
        gsap.fromTo(this._spineLoop, {alpha: 0}, {duration: 0.25, alpha: 1});
    }

    public stopLoop(): void {
        this._spineLoop.state.setEmptyAnimation(0, 0);
        this._spineLoop.visible = false;
        gsap.fromTo(this._spineLoop, {alpha: 1}, {duration: 0.25, alpha: 0});
    }

    public playRiotLoop(): void {
        this.stopLoop();
        this._textCounter.visible = false;
        this._textPost.visible = false;
        this._riotSpine.visible = true;
        this._riotSpine.state.setAnimation(0, "loop", true);
        gsap.fromTo(this._riotSpine, {alpha: 0}, {
            duration: 0.25,
            alpha: 1
        });
    }

    public stopRiotLoop(): void {
        const reelSize: number = this._gameData.Reel.calculateMultiWaysValues(this._gameData.mode)[0];
        PunkRocker.waysCounter.setValue(undefined, reelSize, 0.25);
        this._textPost.visible = true;
        this._textCounter.visible = true;
        this._riotSpine.state.setEmptyAnimation(0, 0);
        this._riotSpine.visible = false;
        gsap.fromTo(this._riotSpine, {alpha: 1}, {
            duration: 0.25,
            alpha: 0
        });
    }

    public hardSetValue(val: number): void {
        this.value = val;
        this.updateValue();
        this.stopLoop();
    }

    public showCrazyMessage(message: string, duration: number): void {
        this._textTimeline = Helper.setTimeline(this._textTimeline);
        this._textPost.position.set(0, 0);
        this._textPost.text = message;
        this._textPost.updateTransform();
        this._textPost.anchor.set(0.5, 0.5);

        this.playLoop();

        this._textTimeline.add([
            gsap.fromTo(this._textPost, {alpha: 0}, {alpha: 1, ease: "power3.inOut", duration: duration * 0.25}),
            gsap.fromTo(this._textPost.scale, {x: 0.5}, {x: 1.2, y: 1.2, duration: duration * 0.25}),
            gsap.fromTo(this._textCounter, {alpha: 1}, {alpha: 0, ease: "power3.inOut", duration: duration * 0.2}),
        ]);

        this._textTimeline.add(AnimationEffects.Shake({
            obj: this._textPost,
            duration: duration,
            Intensity: 4,
            hertz: 130,
            x: 0.6,
            y: 0.7,
            ease: Power3.easeOut
        }), 0)

        this._textTimeline.add([
            gsap.to(this._textPost, {alpha: 0, duration: duration * 0.25, ease: "power3.inOut"}),
            gsap.to(this._textPost.scale, {x: 0.7, y: 0.7, duration: duration * 0.25, ease: "power3.inOut"}),
            gsap.to(this._textCounter, {alpha: 1, duration: duration * 0.25, ease: "power3.inOut"})
        ], duration * 0.75);
        this._textTimeline.play();
    }

    public totalWinMorse(durr: number, fadein: boolean = true): void {
        const tl: gsap.core.Timeline = Helper.setTimeline();
        this._textPost.text = "....  .  .-..  .-..  ---  .--  ---  .-.  .-..  -..";
        this._textPost.updateTransform();
        this._textPost.anchor.set(0.5, 0.5);

        if (fadein) {
            tl.fromTo(this._textPost, {alpha: 0}, {duration: durr, alpha: 1, ease: "power3.inOut"}, 0);
            tl.fromTo(this._textPost.scale, {x: 0.5}, {duration: durr, x: 0.75, y: 0.75}, 0);
            tl.fromTo(this._textCounter, {alpha: 1}, {duration: durr, alpha: 0, ease: "power3.inOut"}, 0);
        } else {
            tl.fromTo(this._textPost, {alpha: 0}, {duration: durr, alpha: 0, ease: "power3.inOut"}, 0);
            tl.fromTo(this._textPost.scale, {x: 0.5}, {duration: durr, x: 0.45, y: 0.45}, 0);
            tl.fromTo(this._textCounter, {alpha: 1}, {duration: durr, alpha: 1, ease: "power3.inOut"}, 0);
        }
        tl.play();
    }

    public FreeSpinsCounter(durr: number, fadein: boolean): void {
        this.tlText = Helper.setTimeline(this.tlText);
        if (fadein) {
            this._textPost.text = this.freeSpins.toString() + " " + Translator.translate(this.freeSpins <= 1 ? TranslationTexts.COUNTER_FS_ONE : TranslationTexts.COUNTER_FS_MANY);
            this._textPost.updateTransform();
            this._textPost.anchor.set(0.5, 0.5);
            this._textPost.scale.set(0.75, 0.75);
            // SlotGame.keypad.setZeroBetSpinCounter(this.freeSpins)
            this.tlText.add([
                gsap.fromTo(this._textPost, {alpha: 0}, {duration: durr, alpha: 0.5, ease: "power3.inOut"}),
                gsap.fromTo(this._textPost.scale, {x: 0.5}, {duration: durr, x: 1, y: 1}),
            ]);
            // tl.add([
            //     () => {this._textPost.text = (this.freeSpins-1).toString() +  " FreeSpins Left."},
            //     () => SlotGame.keypad.setZeroBetSpinCounter(this.freeSpins-1),
            //     gsap.fromTo(this._textPost.scale,duration*0.2,{x:0.9,y:0.9},{x:1,y:1})
            // ], duration*0.8)
        } else {
            this.tlText.add([
                gsap.to(this._textPost, {duration: durr, alpha: 0, ease: "power3.inOut"}),
                gsap.to(this._textPost.scale, {duration: durr, x: 0.45, y: 0.45, ease: "power3.inOut"}),
            ], 0);
        }
        this.tlText.play();
    }

    public counterActive(val: boolean): void {
        this._textCounter.visible = val;
        this._headerBg.visible = val;
        this._spineLoop.visible = val;
    }

    public UpdateFreeSpinCounter(val: number): void {
        this.freeSpins = val;
    }

    public onBottomTrainShow(flag: boolean, isRestore: boolean = false): void {
        this.bottomTrainVisible = flag;
        this.onResize();
    }

    protected createTextObject(): PIXI.Text {
        const textObj = new PIXI.Text(this.value.toString());
        textObj.style = this.DEFAULT_TEXT_STYLE;
        textObj.text += this.waysText;
        textObj.anchor.set(0.5, 0.5);
        this.addChild(textObj);
        this._itemsToHide.push(textObj);

        return textObj;
    }

    protected createHeaderObject(): PIXI.Sprite {
        const headerObj = new PIXI.Sprite(this.headerTexture);
        headerObj.name = "headerBg";
        this.addChild(headerObj);
        headerObj.pivot.set(this.width * 0.5, this.height * 0.5);
        return headerObj;
    }

    private isFirstSpinAfterBonusGame(): boolean {
        const currMode: PRGameMode = this._gameData.mode,
            nextMode: PRGameMode = this._gameData.nextMode;

        let flag: boolean = false

        if (nextMode === PRGameMode.NORMAL && (currMode === PRGameMode.FREESPIN_LEVEL_ONE ||
            currMode === PRGameMode.FREESPIN_LEVEL_TWO ||
            currMode === PRGameMode.FREESPIN_LEVEL_THREE)) {
            flag = true;
        }

        //#386
        if(currMode === PRGameMode.NORMAL && nextMode === PRGameMode.NORMAL && this._textCounter.alpha === 0) {
            flag = true;
        }

        return flag;
    }

    private init(): void {
        this.waysText = " " + Translator.translate(TranslationTexts.BANNER_WAYS);

        this._headerBg = this.createHeaderObject();

        this._spineLoop = new PIXI.spine.Spine(PunkRocker.assetLoader.getSpineAsset("Header"));
        this._spineLoop.name = "spineLoop";
        this._spineLoop.visible = false;
        this.addChild(this._spineLoop);
        this._itemsToHide.push(this._spineLoop);

        this._textCounter = this.createTextObject();
        this._textCounter.name = "textCounter";
        this.updateValue();

        this._textPost = this.createTextObject();
        this._textPost.text = "";
        this._textPost.name = "textPost";
        this._textPost.alpha = 0;
        this._textPost.updateTransform();

        this._riotSpine = new PIXI.spine.Spine(PunkRocker.assetLoader.getSpineAsset("Poster_Riot"));
        this._riotSpine.visible = false;
        this._riotSpine.name = "riotSpine";
        this.addChild(this._riotSpine);
        this._itemsToHide.push(this._riotSpine);
    }
}